import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'S1-1'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '📋',
    category: '변수와 자료 종류',
    learnStepId: 'SA01',
    question: '다음 중 양적 변수(연속형)에 해당하는 것은?\n① 혈액형  ② 학생 수  ③ 키  ④ 좋아하는 과목',
    correctAnswer: '③ 키 (연속형 양적 변수)',
    wrongAnswer: '② 학생 수 (이산형 양적 변수로 혼동)',
    misconceptionCode: 'SP01',
    note: '연속형과 이산형 양적 변수, 질적 변수를 혼동함',
    analysis: {
      diagnosis: '학생 수는 양적 변수이지만 이산형입니다. 키는 소수점으로 측정 가능한 연속형입니다.',
      rootCause: '변수의 종류 분류:\n• 질적 변수(범주형): 혈액형, 성별, 좋아하는 과목\n• 양적 변수(이산형): 학생 수, 자녀 수, 득점\n• 양적 변수(연속형): 키, 몸무게, 시간, 온도\n이산형은 정수만 가능, 연속형은 소수도 가능!',
      correction: '① 혈액형 → 질적 변수 (A, B, O, AB 범주)\n② 학생 수 → 양적 변수(이산형) (소수점 불가)\n③ 키 → 양적 변수(연속형) ✓ (175.3cm 가능)\n④ 좋아하는 과목 → 질적 변수 (범주)',
      example: '연속형 예시: 키(173.5cm), 기온(23.7℃), 달리기 시간(12.34초)\n이산형 예시: 가족 수(3명), 시험 점수(85점)\n질적 예시: 혈액형(O형), 성별(남/여)',
    },
    followUps: [
      { q: '"하루에 마신 물의 양(mL)"은 어떤 변수 유형인가요?', a: '양적 변수(연속형). 물의 양은 소수점 단위로 측정 가능한 연속형 양적 변수입니다.' },
      { q: '"한 달 독서 권 수"는 연속형인가요 이산형인가요?', a: '이산형. 책은 0권, 1권, 2권... 정수 단위로만 셀 수 있습니다.' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '🌿',
    category: '줄기잎 그림 읽기',
    learnStepId: 'SA02',
    question: '줄기잎 그림에서 "3 | 2 5 8"이 있을 때, 이 자료의 값들은?',
    correctAnswer: '32, 35, 38',
    wrongAnswer: '3, 2, 5, 8 또는 325, 358',
    misconceptionCode: 'SP02',
    note: '줄기와 잎의 의미를 잘못 이해함',
    analysis: {
      diagnosis: '줄기잎 그림에서 "3 | 2 5 8"은 줄기 3, 잎 2·5·8로 읽어야 합니다.',
      rootCause: '줄기잎 그림 읽는 법:\n• 줄기(|왼쪽): 십의 자리 숫자\n• 잎(|오른쪽): 일의 자리 숫자\n• 3 | 2 → 32, 3 | 5 → 35, 3 | 8 → 38\n원래 데이터를 그대로 복원할 수 있다!',
      correction: '"3 | 2 5 8" 읽기:\n① 줄기 = 3 (십의 자리)\n② 잎 = 2, 5, 8 (각각 일의 자리)\n③ 자료값 = 32, 35, 38\n\n"1 | 3 7" → 13, 17\n"4 | 0 4 9" → 40, 44, 49',
      example: '줄기잎 그림 예시:\n1 | 5 8\n2 | 0 3 7\n3 | 2 5 8\n→ 자료: 15, 18, 20, 23, 27, 32, 35, 38\n중앙값: 8개 중 4,5번째 평균 = (23+27)/2 = 25',
    },
    followUps: [
      { q: '줄기잎 그림에서 잎을 오름차순으로 정렬하는 이유는?', a: '중앙값·최빈값을 쉽게 파악하기 위해서입니다. 정렬된 줄기잎 그림에서는 가운데 위치의 값이 중앙값이 됩니다.' },
      { q: '"2 | 1 1 3 6 9"에서 최빈값과 중앙값은?', a: '최빈값: 21 (2번 등장), 중앙값: 5개 중 3번째 값 = 23' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '📊',
    category: '도수분포표 계급값',
    learnStepId: 'SA03',
    question: '계급이 "10 이상 20 미만"일 때 계급값은?',
    correctAnswer: '15 (계급값 = (10+20)÷2)',
    wrongAnswer: '10 또는 20 (끝값을 그대로 씀)',
    misconceptionCode: 'SP03',
    note: '계급값의 정의(양 끝값의 평균)를 모름',
    analysis: {
      diagnosis: '계급값은 계급의 양 끝값의 평균입니다. 10이나 20이 아닌 15입니다.',
      rootCause: '계급 관련 용어 정리:\n• 계급의 크기: 상한-하한 = 20-10 = 10\n• 계급값: (하한+상한)÷2 = (10+20)÷2 = 15\n• 도수: 해당 계급에 속하는 자료 수\n계급값은 히스토그램·도수분포다각형에서 x좌표로 사용!',
      correction: '"10 이상 20 미만" 계급 분석:\n① 계급의 크기: 20-10 = 10\n② 계급값: (10+20)÷2 = 30÷2 = 15\n③ 이 계급에 속하는 자료: 10 ≤ x < 20\n   (10 포함, 20 미포함)\n\n히스토그램에서 이 막대의 x좌표 중심 = 15',
      example: '계급값 계산 예시:\n20이상30미만 → (20+30)÷2 = 25\n30이상40미만 → (30+40)÷2 = 35\n40이상50미만 → (40+50)÷2 = 45\n도수분포다각형은 (계급값, 도수) 점을 연결!',
    },
    followUps: [
      { q: '도수분포다각형에서 양 끝에 도수 0인 점을 추가하는 이유는?', a: '그래프가 x축 위에서 닫힌 형태가 되도록 하기 위해서입니다. 이렇게 하면 분포 전체 면적을 시각적으로 파악할 수 있습니다.' },
      { q: '계급의 크기가 5이고 계급값이 32일 때, 이 계급의 범위는?', a: '29.5 이상 34.5 미만. (32-2.5=29.5, 32+2.5=34.5)' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '📈',
    category: '상대도수 계산',
    learnStepId: 'SA04',
    question: '전체 40명 중 한 계급의 도수가 8명일 때, 상대도수는?',
    correctAnswer: '0.2 (8÷40=0.2)',
    wrongAnswer: '20 (백분율로 계산 후 단위 잘못 씀)',
    misconceptionCode: 'SP04',
    note: '상대도수는 비율(0~1)임을 모르고 백분율(%)로 혼동',
    analysis: {
      diagnosis: '"20%"라고 답했지만 상대도수는 0~1 사이의 비율입니다. 백분율과 혼동했습니다.',
      rootCause: '상대도수 vs 백분율 구분:\n• 상대도수 = 도수 ÷ 전체도수 → 0 이상 1 이하\n• 백분율(%) = 상대도수 × 100\n• 8÷40 = 0.2 (상대도수)\n• 0.2 × 100 = 20% (백분율)\n모든 계급의 상대도수 합 = 1 (항상!)',
      correction: '상대도수 계산:\n① 공식: 상대도수 = 도수 ÷ 전체도수\n② 계산: 8 ÷ 40 = 0.2\n③ 정답: 0.2\n\n검증: 모든 계급 상대도수 합 = 1\n백분율로 표현: 0.2 × 100 = 20%\n(상대도수≠백분율! 단위 주의)',
      example: '전체 50명:\n계급A 도수=10 → 상대도수=10÷50=0.2\n계급B 도수=20 → 상대도수=20÷50=0.4\n계급C 도수=15 → 상대도수=15÷50=0.3\n계급D 도수=5  → 상대도수=5÷50=0.1\n합계: 0.2+0.4+0.3+0.1 = 1.0 ✓',
    },
    followUps: [
      { q: '두 반의 수학 점수를 비교할 때 도수 대신 상대도수를 쓰는 이유는?', a: '두 반의 학생 수가 다를 수 있기 때문입니다. 상대도수는 전체를 1로 환산한 비율이므로 집단 크기에 관계없이 공정하게 비교할 수 있습니다.' },
      { q: '어떤 계급의 상대도수가 0.35일 때, 전체 도수가 80이면 이 계급의 도수는?', a: '도수 = 상대도수 × 전체도수 = 0.35 × 80 = 28명' },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '📉',
    category: '누적도수와 누적상대도수',
    learnStepId: 'SA05',
    question: '도수분포표에서 각 계급의 도수가 5, 8, 12, 10, 5일 때, 세 번째 계급까지의 누적도수와 누적상대도수는?',
    correctAnswer: '누적도수=25, 누적상대도수=0.625 (전체 40명)',
    wrongAnswer: '누적도수=12 (세 번째 계급 도수만 씀)',
    misconceptionCode: 'SP05',
    note: '누적도수를 해당 계급의 도수로만 계산하는 오류',
    analysis: {
      diagnosis: '누적도수는 첫 번째 계급부터 해당 계급까지의 도수를 모두 더한 값입니다. 세 번째 계급 도수 12만 쓰면 안 됩니다.',
      rootCause: '누적도수의 의미:\n• 1번째 계급 누적도수: 5\n• 2번째 계급 누적도수: 5+8 = 13\n• 3번째 계급 누적도수: 5+8+12 = 25\n"그 계급 이하에 속하는 모든 자료의 수"!',
      correction: '전체도수 = 5+8+12+10+5 = 40\n\n1계급 누적: 5\n2계급 누적: 5+8 = 13\n3계급 누적: 5+8+12 = 25 ✓\n4계급 누적: 5+8+12+10 = 35\n5계급 누적: 5+8+12+10+5 = 40 (= 전체)\n\n3계급 누적상대도수: 25÷40 = 0.625',
      example: '마지막 계급의 누적도수 = 전체도수 = 40\n마지막 계급의 누적상대도수 = 1\n누적도수를 이용하면: "이 점수 이하인 학생이 몇 %?"를 바로 파악 가능!',
    },
    followUps: [
      { q: '누적도수 그래프(오지브)는 어떤 모양인가요?', a: '왼쪽 아래에서 오른쪽 위로 올라가는 S자 형태(단조증가)입니다. x축은 계급의 상한값, y축은 누적도수입니다.' },
      { q: '누적상대도수가 0.8인 계급은 어떤 의미인가요?', a: '전체 자료의 80%가 그 계급 이하에 속한다는 의미입니다. 즉, 상위 20%가 그 계급보다 높은 계급에 있습니다.' },
    ],
  },
]

// ── PredictedFragmentPanel ────────────────────────────────────────────────────
function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [open, setOpen] = useState(true)
  if (fragments.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
          <Brain size={12} /> AI 예상 취약 개념 ({fragments.length}개)
        </p>
        <button onClick={() => setOpen(o => !o)} className="text-amber-400 hover:text-amber-600">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-1.5">
          {fragments.slice(0, 3).map(f => {
            const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
            return (
              <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
                <span>{f.fragment.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{f.fragment.title}</p>
                  <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function S1_1TutorPage() {
  const [selectedId, setSelectedId] = useState(DEMO_SCENARIOS[0].id)
  const [step, setStep] = useState<'question' | 'wrong' | 'analysis' | 'correction' | 'followup'>('question')
  const [followUpIdx, setFollowUpIdx] = useState(0)
  const [showFollowUpAnswer, setShowFollowUpAnswer] = useState(false)

  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)

  const sc = DEMO_SCENARIOS.find(s => s.id === selectedId)!

  const reset = (id: string) => {
    setSelectedId(id)
    setStep('question')
    setFollowUpIdx(0)
    setShowFollowUpAnswer(false)
  }

  const categoryColor: Record<string, string> = {
    '변수와 자료 종류': 'bg-sky-100 text-sky-700',
    '줄기잎 그림 읽기': 'bg-emerald-100 text-emerald-700',
    '도수분포표 계급값': 'bg-orange-100 text-orange-700',
    '상대도수 계산': 'bg-violet-100 text-violet-700',
    '누적도수와 누적상대도수': 'bg-rose-100 text-rose-700',
  }

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <PredictedFragmentPanel fragments={predictedFragments} />

      {/* 헤더 */}
      <div className="mb-5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Brain size={22} className="text-white" />
          </div>
          <div>
            <h2 className="font-bold text-lg">📊 자료의 정리와 해석 AI 튜터</h2>
            <p className="text-xs text-violet-100">오개념을 진단하고 올바른 이해로 안내합니다</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: '다루는 오개념', value: `${DEMO_SCENARIOS.length}가지` },
            { label: '학습 단계', value: 'SA01~SA05' },
            { label: '추천 시간', value: '20~30분' },
          ].map(item => (
            <div key={item.label} className="bg-white/15 rounded-xl p-2 text-center">
              <p className="text-xs text-violet-100">{item.label}</p>
              <p className="text-sm font-bold">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 시나리오 선택 */}
      <div className="mb-5">
        <p className="text-xs font-bold text-slate-500 mb-2">📋 시나리오 선택</p>
        <div className="space-y-2">
          {DEMO_SCENARIOS.map(s => (
            <button
              key={s.id}
              onClick={() => reset(s.id)}
              className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all ${
                selectedId === s.id
                  ? 'border-violet-400 bg-violet-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{s.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-slate-800">{s.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor[s.category] ?? 'bg-slate-100 text-slate-600'}`}>
                      {s.category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{s.question.split('\n')[0]}</p>
                </div>
                <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 튜터 인터페이스 */}
      <div className="card border-2 border-violet-100">
        {/* 단계 표시 */}
        <div className="flex gap-1 mb-5 overflow-x-auto">
          {(['question', 'wrong', 'analysis', 'correction', 'followup'] as const).map((s, i) => {
            const labels = ['문제', '오답', '진단', '교정', '심화']
            const icons = ['❓', '⚠️', '🔍', '✅', '🚀']
            const active = step === s
            const passed = ['question', 'wrong', 'analysis', 'correction', 'followup'].indexOf(step) > i
            return (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  active ? 'bg-violet-600 text-white' : passed ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-400'
                }`}
              >
                <span>{icons[i]}</span>
                <span>{labels[i]}</span>
              </button>
            )
          })}
        </div>

        {/* 문제 단계 */}
        {step === 'question' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{sc.emoji}</span>
              <div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor[sc.category] ?? 'bg-slate-100 text-slate-600'}`}>
                  {sc.category}
                </span>
                <p className="text-xs text-slate-400 mt-0.5">{sc.note}</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                <MessageCircle size={12} /> 문제
              </p>
              <p className="text-sm font-semibold text-slate-800 whitespace-pre-line">{sc.question}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">먼저 스스로 풀어보세요! 준비되면 아래 버튼을 눌러주세요.</p>
            <button
              onClick={() => setStep('wrong')}
              className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <ChevronRight size={16} /> 오답 예시 보기
            </button>
          </div>
        )}

        {/* 오답 단계 */}
        {step === 'wrong' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-bold text-red-600 mb-2 flex items-center gap-1">
                <XCircle size={12} /> 흔한 오답
              </p>
              <p className="text-sm font-mono text-red-800">{sc.wrongAnswer}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-600 mb-2 flex items-center gap-1">
                <CheckCircle size={12} /> 올바른 답
              </p>
              <p className="text-sm font-mono text-emerald-800">{sc.correctAnswer}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-700">
                <span className="font-bold">오개념 코드: </span>{sc.misconceptionCode} — {sc.note}
              </p>
            </div>
            <button
              onClick={() => setStep('analysis')}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Target size={16} /> AI 진단 보기
            </button>
          </div>
        )}

        {/* 진단 단계 */}
        {step === 'analysis' && (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1">
                <Brain size={12} /> AI 진단
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">{sc.analysis.diagnosis}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <p className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
                <AlertTriangle size={12} /> 오개념 원인
              </p>
              <p className="text-sm text-orange-800 leading-relaxed whitespace-pre-line">{sc.analysis.rootCause}</p>
            </div>
            <button
              onClick={() => setStep('correction')}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} /> 올바른 개념 배우기
            </button>
          </div>
        )}

        {/* 교정 단계 */}
        {step === 'correction' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-600 mb-3 flex items-center gap-1">
                <CheckCircle size={12} /> 올바른 풀이
              </p>
              <p className="text-sm text-emerald-800 leading-relaxed whitespace-pre-line font-mono">{sc.analysis.correction}</p>
            </div>
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <p className="text-xs font-bold text-violet-600 mb-2 flex items-center gap-1">
                <BookOpen size={12} /> 추가 예시
              </p>
              <p className="text-sm text-violet-800 font-mono whitespace-pre-line">{sc.analysis.example}</p>
            </div>
            <button
              onClick={() => { setStep('followup'); setFollowUpIdx(0); setShowFollowUpAnswer(false) }}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles size={16} /> 심화 문제 풀기
            </button>
          </div>
        )}

        {/* 심화 단계 */}
        {step === 'followup' && (
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
                <Sparkles size={12} /> 심화 문제 {followUpIdx + 1}/{sc.followUps.length}
              </p>
              <p className="text-sm font-semibold text-slate-800">{sc.followUps[followUpIdx].q}</p>
            </div>
            {!showFollowUpAnswer ? (
              <button
                onClick={() => setShowFollowUpAnswer(true)}
                className="w-full py-2.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-xl font-semibold text-sm transition-colors"
              >
                정답 확인
              </button>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
                <p className="text-sm text-emerald-800 font-mono whitespace-pre-line">{sc.followUps[followUpIdx].a}</p>
              </div>
            )}
            <div className="flex gap-2">
              {followUpIdx < sc.followUps.length - 1 ? (
                <button
                  onClick={() => { setFollowUpIdx(i => i + 1); setShowFollowUpAnswer(false) }}
                  className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm transition-colors"
                >
                  다음 문제 →
                </button>
              ) : (
                <button
                  onClick={() => reset(sc.id)}
                  className="flex-1 py-2.5 bg-slate-600 hover:bg-slate-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw size={14} /> 처음부터 다시
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 오개념 요약 */}
      <div className="mt-5 card border border-slate-200 bg-slate-50">
        <p className="text-xs font-bold text-slate-600 mb-3 flex items-center gap-1">
          <AlertTriangle size={12} className="text-orange-500" /> 자료의 정리와 해석 주요 오개념 요약
        </p>
        <div className="space-y-2">
          {[
            { code: 'SP01', title: '변수 유형 혼동', desc: '연속형(키·기온) vs 이산형(학생수) vs 질적(혈액형) 구별 필수!' },
            { code: 'SP02', title: '줄기잎 그림 읽기 오류', desc: '"3|2 5 8" → 32, 35, 38. 줄기=십의자리, 잎=일의자리!' },
            { code: 'SP03', title: '계급값 정의 혼동', desc: '계급값 = (하한+상한)÷2. 끝값이 아닌 중간값!' },
            { code: 'SP04', title: '상대도수와 백분율 혼동', desc: '상대도수 = 도수÷전체(0~1). 백분율 = 상대도수×100(%)' },
            { code: 'SP05', title: '누적도수 계산 오류', desc: '누적도수 = 처음부터 해당 계급까지 도수 합. 해당 계급만 쓰면 안 됨!' },
          ].map(item => (
            <div key={item.code} className="flex items-start gap-2 p-2 bg-white rounded-lg border border-slate-100">
              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0">
                {item.code}
              </span>
              <div>
                <p className="text-xs font-semibold text-slate-700">{item.title}</p>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
